import { useTranslation } from "react-i18next"
import { VideoCamera } from "@phosphor-icons/react"
import { Button } from "@/components/ui/button"
import { TooltipContent, TooltipCustom, TooltipPortal, TooltipTrigger } from "@/components/ui/tooltip"
import { isMedgateConnect } from "@/modules/patients/agenda/teleconsultation/booking/constants/medgate-connect"
import { useOpenNewWindow } from "@/modules/patients/detail/progress-note/hooks/useOpenNewWindow"
import { useAppointmentParam } from "@/modules/patients/hooks/useAppointmentParam"
import { usePartnerInVideoCallChannel } from "@/modules/video-call/services/usePartnerInVideoCallChannel"
import { useVideoCallPickupErrorListener } from "@/modules/video-call/services/useVideoCallPickupErrorListener"
import { Access, Language, Service } from "@/types/__generated__/union-types"

interface PartnerVideoCallButtonProps {
  progressNoteId: string
  language: Language
  access: Access
  service: Service
  isPartnerInVideoCall: boolean
}

const PartnerVideoCallButton = ({
  progressNoteId,
  language,
  access,
  service,
  isPartnerInVideoCall,
}: PartnerVideoCallButtonProps) => {
  useVideoCallPickupErrorListener()

  const isEligible = access === "PARTNER_PORTAL" && isMedgateConnect(service)

  if (!isEligible) return null

  return (
    <EligiblePartnerVideoCallButton
      progressNoteId={progressNoteId}
      language={language}
      isPartnerInVideoCall={isPartnerInVideoCall}
    />
  )
}

interface EligiblePartnerVideoCallButtonProps {
  progressNoteId: string
  language: Language
  isPartnerInVideoCall: boolean
}

const EligiblePartnerVideoCallButton = ({
  progressNoteId,
  language,
  isPartnerInVideoCall,
}: EligiblePartnerVideoCallButtonProps) => {
  const { t } = useTranslation()
  const appointmentId = useAppointmentParam()
  const openNewWindow = useOpenNewWindow()
  usePartnerInVideoCallChannel()

  if (!isPartnerInVideoCall) return null

  const handleClick = () => {
    const url = `/video-call?appointmentId=${appointmentId}&progressNoteId=${progressNoteId}&language=${language}&intent=pickupPartner`
    openNewWindow(url, "sofia-partner-video-call")
  }

  return (
    <TooltipCustom>
      <TooltipTrigger asChild>
        <Button
          className="bg-success hover:bg-success-hovered shrink-0"
          onClick={handleClick}
          data-testid="partner-video-call-btn">
          <VideoCamera size={20} className="text-on-success" />
          <span>{t("videoCall.join")}</span>
        </Button>
      </TooltipTrigger>
      <TooltipPortal>
        <TooltipContent side="bottom" align="center">
          {t("videoCall.partnerInVideoCall")}
        </TooltipContent>
      </TooltipPortal>
    </TooltipCustom>
  )
}

export { PartnerVideoCallButton }
